import { Bell, BellOff, Loader2 } from 'lucide-react';
import { usePushSetup } from '../../hooks/usePushSetup';
import type { PushDriver, PushSetupStatus } from '../../lib/push-setup';
import { cn } from '../../lib/utils';

type Copy = {
  icon: typeof Bell;
  title: string;
  body: string;
  /** Absent when no in-app action can succeed — a button that cannot work is worse than none. */
  action?: string;
  footnote?: string;
};

const COPY: Partial<Record<PushSetupStatus, Copy>> = {
  'never-enabled': {
    icon: Bell,
    title: 'Get alerts when you look away',
    // A real, checkable limitation — not manufactured urgency.
    body: 'Notifications only reach you while this tab is open. Desktop alerts show up even when it is closed.',
    action: 'Turn on desktop alerts',
    footnote: 'One click, and you can turn it off any time.',
  },
  'device-missing': {
    icon: Bell,
    // Names the step already done, because it genuinely was.
    title: 'One step left for this browser',
    body: 'Desktop alerts are already on for your account — this browser just needs registering.',
    action: 'Register this browser',
    footnote: 'Each browser registers separately.',
  },
  blocked: {
    icon: BellOff,
    title: 'Your browser is blocking alerts',
    body: 'Notifications are denied for this site at the browser level. Re-allow them in your browser settings, then check again.',
    footnote: 'Nothing is lost meanwhile — notifications still collect here.',
  },
};

export interface PushSetupPromptViewProps {
  status: PushSetupStatus;
  shouldPrompt: boolean;
  enable: () => void;
  dismiss: () => void;
  refresh: () => void;
  busy?: boolean;
  error?: string | null;
  className?: string;
}

/**
 * Presentational half, so the extension can render the same prompt from its own
 * data layer. The extension resolves its workspace through `browser.storage` and
 * its own tRPC proxy, so it cannot call `usePushSetup` — but the copy and the
 * state-to-CTA mapping are the parts worth sharing, and they live here.
 *
 * Sits above the notification list, never in place of it: the inbox is useful
 * whether or not push is on, so this is an upgrade offer rather than a gate.
 * Always dismissible.
 */
export function PushSetupPromptView({
  status,
  shouldPrompt,
  enable,
  dismiss,
  refresh,
  busy = false,
  error = null,
  className,
}: PushSetupPromptViewProps) {
  const copy = COPY[status];

  if (!shouldPrompt || !copy) return null;

  const Icon = copy.icon;

  return (
    <div className={cn('border-b border-border bg-primary/5 p-3', className)}>
      <div className="flex gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{copy.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{copy.body}</p>

          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}

          <div className="mt-2 flex items-center gap-2">
            {copy.action ? (
              <button
                type="button"
                onClick={() => void enable()}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                {busy ? 'Turning on…' : copy.action}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void refresh()}
                className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90"
              >
                Check again
              </button>
            )}
            <button
              type="button"
              onClick={() => void dismiss()}
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Not now
            </button>
          </div>

          {copy.footnote && (
            <p className="mt-1.5 text-[11px] text-muted-foreground/80">{copy.footnote}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export interface PushSetupPromptProps {
  driver: PushDriver;
  className?: string;
}

/** Portal-side wiring: the view above, driven by the shared policy hook. */
export function PushSetupPrompt({ driver, className }: PushSetupPromptProps) {
  const { status, shouldPrompt, enable, dismiss, refresh, busy, error } = usePushSetup(driver);

  return (
    <PushSetupPromptView
      status={status}
      shouldPrompt={shouldPrompt}
      enable={() => void enable()}
      dismiss={() => void dismiss()}
      refresh={() => void refresh()}
      busy={busy}
      error={error}
      className={className}
    />
  );
}
